/**
 * Web Audio API Procedural Sound Synthesizer
 * Produces soft, elegant, warm institutional sounds (gentle clicks, soft bell chimes, warm harmonic fanfare).
 */

class SoundSynthesizer {
    private ctx: AudioContext | null = null;
    private isMuted: boolean = false;

    private getContext(): AudioContext | null {
        if (typeof window === 'undefined') return null;
        if (!this.ctx) {
            const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
            if (AudioCtx) {
                this.ctx = new AudioCtx();
            }
        }
        if (this.ctx && this.ctx.state === 'suspended') {
            this.ctx.resume().catch(() => {});
        }
        return this.ctx;
    }

    public toggleMute(): boolean {
        this.isMuted = !this.isMuted;
        return this.isMuted;
    }

    public setMuted(muted: boolean) {
        this.isMuted = muted;
    }

    public getIsMuted(): boolean {
        return this.isMuted;
    }

    /**
     * Soft, organic acoustic tick during spinning
     */
    public playTick(pitch: number = 440) {
        if (this.isMuted) return;
        const ctx = this.getContext();
        if (!ctx) return;

        try {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            const filter = ctx.createBiquadFilter();

            filter.type = 'lowpass';
            filter.frequency.setValueAtTime(1200, ctx.currentTime);

            osc.type = 'sine';
            osc.frequency.setValueAtTime(pitch, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(pitch * 0.7, ctx.currentTime + 0.03);

            gain.gain.setValueAtTime(0.04, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.03);

            osc.connect(filter);
            filter.connect(gain);
            gain.connect(ctx.destination);

            osc.start();
            osc.stop(ctx.currentTime + 0.035);
        } catch {
            // Ignore audio errors
        }
    }

    /**
     * Soft atmospheric swell before reveal
     */
    public playRiser(durationMs: number = 1400) {
        if (this.isMuted) return;
        const ctx = this.getContext();
        if (!ctx) return;

        try {
            const durationSec = durationMs / 1000;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            const filter = ctx.createBiquadFilter();

            filter.type = 'lowpass';
            filter.frequency.setValueAtTime(400, ctx.currentTime);
            filter.frequency.linearRampToValueAtTime(1000, ctx.currentTime + durationSec);

            osc.type = 'sine';
            osc.frequency.setValueAtTime(180, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(320, ctx.currentTime + durationSec);

            gain.gain.setValueAtTime(0.005, ctx.currentTime);
            gain.gain.linearRampToValueAtTime(0.035, ctx.currentTime + durationSec * 0.7);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationSec);

            osc.connect(filter);
            filter.connect(gain);
            gain.connect(ctx.destination);

            osc.start();
            osc.stop(ctx.currentTime + durationSec + 0.05);
        } catch {
            // Ignore
        }
    }

    /**
     * Warm, gentle bell chime when a number locks in
     */
    public playLock(index: number = 0) {
        if (this.isMuted) return;
        const ctx = this.getContext();
        if (!ctx) return;

        try {
            const frequencies = [523.25, 587.33, 659.25, 783.99, 880.00, 1046.50];
            const baseFreq = frequencies[index % frequencies.length];

            // Fundamental
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.type = 'sine';
            osc1.frequency.setValueAtTime(baseFreq, ctx.currentTime);

            gain1.gain.setValueAtTime(0.08, ctx.currentTime);
            gain1.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);

            osc1.connect(gain1);
            gain1.connect(ctx.destination);

            osc1.start();
            osc1.stop(ctx.currentTime + 0.2);

            // Soft harmonic overtone
            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.type = 'sine';
            osc2.frequency.setValueAtTime(baseFreq * 2, ctx.currentTime);

            gain2.gain.setValueAtTime(0.02, ctx.currentTime);
            gain2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);

            osc2.connect(gain2);
            gain2.connect(ctx.destination);

            osc2.start();
            osc2.stop(ctx.currentTime + 0.15);
        } catch {
            // Ignore
        }
    }

    /**
     * Elegant, warm acoustic celebratory chime on prize reveal
     */
    public playFanfare() {
        if (this.isMuted) return;
        const ctx = this.getContext();
        if (!ctx) return;

        try {
            // Soft marimba / harp chord: C5 -> E5 -> G5 -> C6
            const notes = [523.25, 659.25, 783.99, 1046.50];
            notes.forEach((freq, idx) => {
                const startTime = ctx.currentTime + idx * 0.08;
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();

                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, startTime);

                gain.gain.setValueAtTime(0.001, startTime);
                gain.gain.linearRampToValueAtTime(0.07, startTime + 0.03);
                gain.gain.exponentialRampToValueAtTime(0.0001, startTime + 0.85);

                osc.connect(gain);
                gain.connect(ctx.destination);

                osc.start(startTime);
                osc.stop(startTime + 0.9);
            });
        } catch {
            // Ignore
        }
    }
}

export const soundFx = new SoundSynthesizer();
